/**
 * Idioma del propietario en captaciones (datos_extras / propietarios_ficha).
 * Alineado con backend encargo-i18n.resolverIdiomaPropietario1.
 */
import {
  LEAD_IDIOMA_OPTIONS,
  normalizeLanguageCode,
  formatIdiomaBadge,
} from "./leadLanguage";

export {
  LEAD_IDIOMA_OPTIONS as CAPTACION_IDIOMA_OPTIONS,
  normalizeLanguageCode,
  formatIdiomaBadge,
};

function parseExtras(extras) {
  if (!extras) return {};
  if (typeof extras === "object" && !Array.isArray(extras)) return extras;
  if (typeof extras === "string") {
    try {
      const o = JSON.parse(extras);
      return o && typeof o === "object" && !Array.isArray(o) ? o : {};
    } catch {
      return {};
    }
  }
  return {};
}

/** Código corto: es, en, fr, … (default es). */
export function resolverIdiomaPropietarioCaptacion(captacionOrExtras) {
  if (!captacionOrExtras) return "es";
  const ex = captacionOrExtras.datos_extras != null
    ? parseExtras(captacionOrExtras.datos_extras)
    : parseExtras(captacionOrExtras);

  const ficha = Array.isArray(ex.propietarios_ficha) ? ex.propietarios_ficha : [];
  const titular =
    ficha.find((p) => p && (p.id === "titular" || p.es_titular === true))
    || ficha[0];
  if (titular?.idioma) {
    const c = normalizeLanguageCode(titular.idioma);
    if (c) return c;
  }

  const extraOwners = Array.isArray(ex.propietarios_extra) ? ex.propietarios_extra : [];
  for (const p of extraOwners) {
    const c = normalizeLanguageCode(p?.idioma);
    if (c) return c;
  }

  const fromExtras = normalizeLanguageCode(ex.idioma);
  if (fromExtras) return fromExtras;

  const campos = ex.campos && typeof ex.campos === "object" ? ex.campos : {};
  const fromCampos = normalizeLanguageCode(campos.idioma);
  if (fromCampos) return fromCampos;

  const fromRow = normalizeLanguageCode(captacionOrExtras.idioma);
  if (fromRow) return fromRow;

  return "es";
}

/** Parche para datos_extras al cambiar idioma del titular. */
export function patchIdiomaEnExtrasCaptacion(prevExtras, idiomaCode) {
  const code = normalizeLanguageCode(idiomaCode) || "es";
  const ex = parseExtras(prevExtras);
  const out = { ...ex, idioma: code };
  if (Array.isArray(ex.propietarios_ficha) && ex.propietarios_ficha.length) {
    out.propietarios_ficha = ex.propietarios_ficha.map((p, i) => {
      if (!p || typeof p !== "object") return p;
      const isTit = p.id === "titular" || p.es_titular === true || i === 0;
      return isTit ? { ...p, idioma: code } : p;
    });
  }
  return out;
}

/**
 * Respuestas rápidas de captación (interés / cita / seguimiento)
 * en el idioma del propietario. El saludo con datos del inmueble
 * lo añade PanelHistorialCaptacion cuando aplica.
 */
const CAPTACION_WA_QUICK_I18N = {
  es: {
    interes: "Hola {nombre}, me pongo en contacto contigo porque tenemos clientes interesados en inmuebles de tu zona. ¿Estarías dispuesto a explorar una posible venta o alquiler?",
    cita: "Hola {nombre}, ¿tendrías disponibilidad esta semana para que pasemos por tu inmueble y podamos valorarlo sin ningún compromiso?",
    seguimiento: "Hola {nombre}, te escribo para hacer un seguimiento de nuestra conversación anterior. ¿Has tenido tiempo de pensarlo? Quedo a tu disposición.",
  },
  en: {
    interes: "Hi {nombre}, I'm reaching out because we have clients interested in properties in your area. Would you be open to exploring a possible sale or rental?",
    cita: "Hi {nombre}, would you have availability this week for us to visit your property and give you a valuation, with no obligation?",
    seguimiento: "Hi {nombre}, I'm following up on our previous conversation. Have you had a chance to think it over? I'm here if you need anything.",
  },
  fr: {
    interes: "Bonjour {nombre}, je vous contacte car nous avons des clients intéressés par des biens dans votre secteur. Seriez-vous ouvert(e) à envisager une vente ou une location ?",
    cita: "Bonjour {nombre}, auriez-vous de la disponibilité cette semaine pour que nous passions voir votre bien et en fassions une estimation, sans engagement ?",
    seguimiento: "Bonjour {nombre}, je me permets de revenir vers vous suite à notre échange. Avez-vous eu le temps d'y réfléchir ? Je reste à votre disposition.",
  },
  de: {
    interes: "Hallo {nombre}, ich melde mich, weil wir Interessenten für Immobilien in Ihrer Gegend haben. Wären Sie offen für einen möglichen Verkauf oder eine Vermietung?",
    cita: "Hallo {nombre}, hätten Sie diese Woche Zeit, damit wir Ihre Immobilie besichtigen und unverbindlich bewerten können?",
    seguimiento: "Hallo {nombre}, ich möchte an unser vorheriges Gespräch anknüpfen. Hatten Sie schon Zeit, darüber nachzudenken? Ich stehe Ihnen gerne zur Verfügung.",
  },
  nl: {
    interes: "Hallo {nombre}, ik neem contact op omdat we klanten hebben die geïnteresseerd zijn in woningen in uw buurt. Zou u openstaan voor een mogelijke verkoop of verhuur?",
    cita: "Hallo {nombre}, heeft u deze week tijd zodat we uw woning kunnen bekijken en vrijblijvend kunnen waarderen?",
    seguimiento: "Hallo {nombre}, ik kom even terug op ons eerdere gesprek. Heeft u er al over nagedacht? Ik help u graag verder.",
  },
  ca: {
    interes: "Hola {nombre}, em poso en contacte amb tu perquè tenim clients interessats en immobles de la teua zona. Estaries disposat/da a explorar una possible venda o lloguer?",
    cita: "Hola {nombre}, tindries disponibilitat aquesta setmana perquè passem pel teu immoble i el puguem valorar sense cap compromís?",
    seguimiento: "Hola {nombre}, t'escric per fer un seguiment de la nostra conversa anterior. Has tingut temps de pensar-ho? Quede a la teua disposició.",
  },
  ru: {
    interes: "Здравствуйте, {nombre}! Связываюсь с вами, потому что у нас есть клиенты, заинтересованные в объектах в вашем районе. Были бы вы готовы рассмотреть возможную продажу или аренду?",
    cita: "Здравствуйте, {nombre}! Были бы вы свободны на этой неделе, чтобы мы могли приехать и оценить ваш объект без обязательств?",
    seguimiento: "Здравствуйте, {nombre}! Пишу, чтобы продолжить наш предыдущий разговор. Удалось ли вам подумать? Я к вашим услугам.",
  },
};

export function buildCaptacionWaQuickReplies(idiomaCode) {
  const code = normalizeLanguageCode(idiomaCode) || "es";
  const pack = CAPTACION_WA_QUICK_I18N[code] || CAPTACION_WA_QUICK_I18N.es;
  return [
    { id: "interes", emoji: "🏡", label: "Interés inmueble", text: pack.interes },
    { id: "cita", emoji: "📅", label: "Proponer cita", text: pack.cita },
    { id: "seguimiento", emoji: "🔄", label: "Seguimiento", text: pack.seguimiento },
  ];
}
